import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { useState, useEffect, createContext, useContext, useCallback } from "react";
import axios from "axios";
import { Toaster } from "sonner";

// Pages
import HomePage from "./pages/HomePage";
import InstantQuotePage from "./pages/InstantQuotePage";
import BookSurveyPage from "./pages/BookSurveyPage";
import ServicesPage from "./pages/ServicesPage";
import HouseRemovalsPage from "./pages/HouseRemovalsPage";
import OfficeRemovalsPage from "./pages/OfficeRemovalsPage";
import StoragePage from "./pages/StoragePage";
import SpecialistMovesPage from "./pages/SpecialistMovesPage";
import ManAndVanPage from "./pages/ManAndVanPage";
import AreasPage from "./pages/AreasPage";
import CardiffPage from "./pages/areas/CardiffPage";
import NewportPage from "./pages/areas/NewportPage";
import SwanseaPage from "./pages/areas/SwanseaPage";
import AboutPage from "./pages/AboutPage";
import ReviewsPage from "./pages/ReviewsPage";
import FAQPage from "./pages/FAQPage";
import ContactPage from "./pages/ContactPage";
import TermsPage from "./pages/TermsPage";
import PrivacyPage from "./pages/PrivacyPage";
import CookiesPage from "./pages/CookiesPage";
import PaymentPage from "./pages/PaymentPage";
import PaymentSuccessPage from "./pages/PaymentSuccessPage";
import NotFoundPage from "./pages/NotFoundPage";

// Admin Pages
import AdminLoginPage from "./pages/admin/AdminLoginPage";
import AdminDashboard from "./pages/admin/AdminDashboard";
import AdminQuotes from "./pages/admin/AdminQuotes";
import AdminBookings from "./pages/admin/AdminBookings";
import AdminCustomers from "./pages/admin/AdminCustomers";
import AdminSurveys from "./pages/admin/AdminSurveys";
import AdminCalendar from "./pages/admin/AdminCalendar";
import AdminPricingRules from "./pages/admin/AdminPricingRules";

import "@/App.css";

import StickyMobileCTA from "./components/shared/StickyMobileCTA";
import WhatsAppButton from "./components/shared/WhatsAppButton";
import LeadCapturePopup from "./components/shared/LeadCapturePopup";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// In production with custom domain, use same-origin to avoid cross-domain cookie issues
const API_BASE = (() => {
  if (typeof window !== 'undefined') {
    const host = window.location.hostname;
    // If on custom domain, use same-origin (cookies stay first-party)
    if (host === 'www.hitechtransit.com' || host === 'hitechtransit.com') {
      return `${window.location.origin}/api`;
    }
  }
  return `${BACKEND_URL}/api`;
})();

// Create axios instance
export const api = axios.create({
  baseURL: API_BASE,
  withCredentials: true,
});

// Auth Context
export const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
};

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const response = await api.get("/auth/me");
      setUser(response.data);
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password) => {
    const response = await api.post("/auth/login", { email, password });
    const userData = response.data;
    setUser(userData);
    return userData;
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch {
      // Expected when session already expired — proceed with client-side cleanup
      void 0;
    }
    setUser(null);
  };

  const processOAuthSession = useCallback(async (sessionId) => {
    const response = await api.post("/auth/session", { session_id: sessionId });
    const userData = response.data;
    setUser(userData);
    return userData;
  }, []);

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, processOAuthSession, checkAuth }}>
      {children}
    </AuthContext.Provider>
  );
};

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#FF5A1F]"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/admin/login" state={{ from: location }} replace />;
  }

  return children;
};

// OAuth Callback Handler
const AuthCallback = () => {
  const { processOAuthSession } = useAuth();
  const [error, setError] = useState(null);
  
  useEffect(() => {
    const handleCallback = async () => {
      const hash = window.location.hash;
      const sessionIdMatch = hash.match(/session_id=([^&]+)/);
      
      if (sessionIdMatch) {
        try {
          await processOAuthSession(sessionIdMatch[1]);
          // Clear hash and redirect
          window.history.replaceState(null, "", "/admin");
          window.location.href = "/admin";
        } catch (err) {
          setError("Authentication failed. Please try again.");
        }
      }
    };
    
    handleCallback();
  }, [processOAuthSession]);

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-center">
          <p className="text-red-600 mb-4">{error}</p>
          <a href="/admin/login" className="text-[#FF5A1F] hover:underline">Back to Login</a>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50">
      <div className="text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#FF5A1F] mx-auto mb-4"></div>
        <p className="text-slate-600">Completing authentication...</p>
      </div>
    </div>
  );
};

function AppRouter() {
  const location = useLocation();
  
  // Check for OAuth callback
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      {/* Public Pages */}
      <Route path="/" element={<HomePage />} />
      <Route path="/instant-quote" element={<InstantQuotePage />} />
      <Route path="/book-survey" element={<BookSurveyPage />} />
      <Route path="/services" element={<ServicesPage />} />
      <Route path="/services/house-removals" element={<HouseRemovalsPage />} />
      <Route path="/services/office-removals" element={<OfficeRemovalsPage />} />
      <Route path="/services/storage" element={<StoragePage />} />
      <Route path="/services/specialist-moves" element={<SpecialistMovesPage />} />
      <Route path="/services/man-and-van" element={<ManAndVanPage />} />
      <Route path="/areas-we-cover" element={<AreasPage />} />
      <Route path="/areas/cardiff" element={<CardiffPage />} />
      <Route path="/areas/newport" element={<NewportPage />} />
      <Route path="/areas/swansea" element={<SwanseaPage />} />
      <Route path="/about" element={<AboutPage />} />
      <Route path="/reviews" element={<ReviewsPage />} />
      <Route path="/faq" element={<FAQPage />} />
      <Route path="/contact" element={<ContactPage />} />
      <Route path="/terms" element={<TermsPage />} />
      <Route path="/privacy" element={<PrivacyPage />} />
      <Route path="/cookies" element={<CookiesPage />} />
      <Route path="/payment/:bookingId" element={<PaymentPage />} />
      <Route path="/payment/success" element={<PaymentSuccessPage />} />

      {/* Admin Pages */}
      <Route path="/admin/login" element={<AdminLoginPage />} />
      <Route path="/admin" element={<ProtectedRoute><AdminDashboard /></ProtectedRoute>} />
      <Route path="/admin/quotes" element={<ProtectedRoute><AdminQuotes /></ProtectedRoute>} />
      <Route path="/admin/bookings" element={<ProtectedRoute><AdminBookings /></ProtectedRoute>} />
      <Route path="/admin/customers" element={<ProtectedRoute><AdminCustomers /></ProtectedRoute>} />
      <Route path="/admin/surveys" element={<ProtectedRoute><AdminSurveys /></ProtectedRoute>} />
      <Route path="/admin/calendar" element={<ProtectedRoute><AdminCalendar /></ProtectedRoute>} />
      <Route path="/admin/pricing-rules" element={<ProtectedRoute><AdminPricingRules /></ProtectedRoute>} />
      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Toaster position="top-right" richColors />
        <AppRouter />
        <StickyMobileCTA />
        <WhatsAppButton />
        <LeadCapturePopup />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
